import { Eyebrow } from '@/components/ui';
import { AlertIcon, BarbellIcon, FlameIcon, TargetIcon } from '@/lib/icons';

const AUDIENCES = [
  {
    name: 'Coaches',
    line: 'One-to-one clients, run properly.',
    body: 'A roster of named clients, each with their own programme and grid you can read, edit and prescribe into — plus their compliance, adherence, PRs and flags in one place.',
    icon: <TargetIcon className="text-lg" />,
  },
  {
    name: 'Performance gyms',
    line: 'A team of coaches, one client list.',
    body: 'Seats for every client your plan allows, availability slots your coaches publish, and revenue by period and by coach.',
    icon: <BarbellIcon className="text-lg" />,
  },
  {
    name: 'Recovery centres',
    line: 'Programmes and appointments side by side.',
    body: 'Prescribe mobility, stability and low-impact work, book clients into individual or shared sessions, and keep the pain flags in view.',
    icon: <FlameIcon className="text-lg" />,
  },
];

/**
 * Who the CRM is for, and the problem it answers. The audience is coaches and
 * performance/recovery gyms — deliberately not regular gyms (platform plan §2).
 */
export function CrmAudience() {
  return (
    <section id="crm-who" className="border-b border-hairline bg-page-subtle py-20">
      <div className="mx-auto max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] px-4 sm:px-6">
        <div className="mb-10 flex flex-col gap-4">
          <Eyebrow>Who it’s for</Eyebrow>
          <h2 className="font-display text-h2 font-black uppercase leading-tight tracking-[-0.01em] text-ink md:text-display-2">
            A coaching workspace, not a gym front desk.
          </h2>
          <p className="max-w-[74ch] text-body-lg text-ink-muted">
            The CRM is for the people who run programmes for named clients — coaches, performance gyms and
            recovery centres. A regular gym that only sells access does not need it; a
            coach who is accountable for a client’s next block does.
          </p>
        </div>

        <ul className="grid gap-6 md:grid-cols-3">
          {AUDIENCES.map((audience) => (
            <li
              key={audience.name}
              className="flex flex-col gap-4 rounded-card border border-hairline bg-surface p-6 shadow-card"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center bg-brand-charcoal text-brand-white">
                {audience.icon}
              </span>
              <h3 className="font-display text-[17px] font-black uppercase tracking-[-0.01em] text-ink">
                {audience.name}
              </h3>
              <p className="text-[13px] font-bold text-accent">{audience.line}</p>
              <p className="text-[13px] leading-relaxed text-ink-muted">{audience.body}</p>
            </li>
          ))}
        </ul>

        <div className="mt-10 flex flex-col gap-4 rounded-card border border-hairline bg-surface p-6 shadow-card lg:flex-row lg:items-start lg:gap-6">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center border border-hairline text-accent">
            <AlertIcon className="text-lg" />
          </span>
          <div className="flex flex-col gap-2">
            <h3 className="font-display text-[18px] font-black uppercase tracking-[-0.01em] text-ink">
              The problem: you run clients from spreadsheets.
            </h3>
            <p className="max-w-[86ch] text-[14px] leading-relaxed text-ink-muted">
              A programme in one file, bookings in a calendar, payments in a bank app, and no reliable answer to
              which client is stalling or who owes you money. The CRM puts the roster, the programmes, the
              calendar and the revenue in one workspace — on the same data model as the lifter’s own app, so a
              change on either side lands on both.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
